/**
 * JobTailoringPurchaseModal Component
 *
 * Handles the complete purchase flow for job tailoring.
 * Shows pricing comparison, then embeds PaymentForm for checkout.
 * Supports coupon/promotion codes for discounts.
 */

import React, { useState } from 'react';
import { ArrowLeft, Check, Star } from 'lucide-react';
import { BaseModal } from './BaseModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PaymentForm } from '../payments/PaymentForm';
import { stripeService, PurchaseResponse } from '../../services';
import { cn } from '../../lib/utils';
import { BRAND_GRADIENT } from '../../lib/brand';

interface JobTailoringPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void; // Called after successful payment
}

type PurchaseFlow = 'select' | 'payment-single' | 'payment-subscription' | 'processing' | 'success';

const CREDIT_FEATURES = [
  'Comprehensive processing and analysis of the target job posting against your portfolio',
  'Identification of the matches + gaps between your listed experience and the job requirements',
  'Remediation of gaps through conversation with a specialized AI agent*',
  'Generation of (1) resume specifically tailored to the target job posting',
  'Fine tuning of your resume with a specialized AI agent*',
  'Sharing of your Personal Advocate to employers with a custom link',
];

export const JobTailoringPurchaseModal: React.FC<JobTailoringPurchaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [flow, setFlow] = useState<PurchaseFlow>('select');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Coupon state
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponApplied, setCouponApplied] = useState<{
    coupon_id: string;
    name?: string;
    discount: string;
  } | null>(null);
  const [finalAmount, setFinalAmount] = useState<number | null>(null);
  const [originalAmount, setOriginalAmount] = useState<number | null>(null);

  const handleSelectSingle = async () => {
    setFlow('processing');
    setError(null);

    try {
      const result: PurchaseResponse = await stripeService.createJobCreditIntent(
        couponCode.trim() || undefined
      );

      // Handle zero-cost purchase (100% off coupon)
      if (!result.payment_required) {
        setCouponApplied(result.coupon_applied || null);
        setFlow('success');
        setTimeout(() => {
          onSuccess();
          handleReset();
        }, 2000);
        return;
      }

      // Normal payment flow
      setClientSecret(result.client_secret || null);
      setFinalAmount(result.amount);
      setOriginalAmount(result.original_amount || null);
      setCouponApplied(result.coupon_applied || null);
      setFlow('payment-single');
    } catch (err: any) {
      setError(err.message || 'Failed to create payment. Please try again.');
      setFlow('select');
    }
  };

  const handleSelectSubscription = async () => {
    setFlow('processing');
    setError(null);

    try {
      const result: PurchaseResponse = await stripeService.createSubscription(
        couponCode.trim() || undefined
      );

      // Handle zero-cost subscription (100% off coupon with forever duration)
      if (!result.payment_required) {
        setCouponApplied(result.coupon_applied || null);
        setFlow('success');
        setTimeout(() => {
          onSuccess();
          handleReset();
        }, 2000);
        return;
      }

      // Normal payment flow
      setClientSecret(result.client_secret || null);
      setFinalAmount(result.amount);
      setOriginalAmount(result.original_amount || null);
      setCouponApplied(result.coupon_applied || null);
      setFlow('payment-subscription');
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription. Please try again.');
      setFlow('select');
    }
  };

  const handlePaymentSuccess = () => {
    setFlow('success');
    setTimeout(() => {
      onSuccess();
      handleReset();
    }, 2000);
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleReset = () => {
    setFlow('select');
    setClientSecret(null);
    setError(null);
    setCouponCode('');
    setCouponApplied(null);
    setFinalAmount(null);
    setOriginalAmount(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Determine modal title and maxWidth based on flow
  const getModalConfig = () => {
    if (flow === 'select' || flow === 'processing') {
      return { title: 'Choose Your Plan', maxWidth: '4xl' as const };
    }
    if (flow === 'payment-single') {
      return { title: 'Complete Purchase', maxWidth: 'md' as const };
    }
    if (flow === 'payment-subscription') {
      return { title: 'Complete Subscription', maxWidth: 'md' as const };
    }
    if (flow === 'success') {
      return { title: '', maxWidth: 'sm' as const, showCloseButton: false };
    }
    return { title: 'Choose Your Plan', maxWidth: '4xl' as const };
  };

  const modalConfig = getModalConfig();

  // Calculate payment form variables
  const displayAmount = finalAmount ?? (flow === 'payment-single' ? 299 : 1499);
  const paymentDescription =
    flow === 'payment-single' ? 'Job Tailoring Credit' : 'Monthly Subscription';

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth={modalConfig.maxWidth}
      title={modalConfig.title}
      showCloseButton={modalConfig.showCloseButton}
    >
      {/* Show selection view */}
      {(flow === 'select' || flow === 'processing') && (
        <div className="space-y-6">
          {error && (
            <div className="rounded-xl border border-destructive/25 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          )}

          <p className="text-foreground/70">
            Choose the plan that best fits your job search needs
          </p>

          {/* Pricing Comparison */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Single Job Credit */}
            <div className="flex h-full flex-col rounded-2xl border border-border bg-card/80 p-6 sm:p-7">
              <h3 className="text-lg font-semibold">Job Tailoring Credit</h3>
              <div className="mt-3 flex items-baseline gap-2.5">
                <span className="text-4xl font-bold tracking-tight">$2.99</span>
                <span className="text-sm text-foreground/45 line-through">$4.99</span>
                <span className="text-xs font-semibold tracking-[0.1em] text-success uppercase">
                  early access
                </span>
              </div>
              <p className="mt-4 text-sm text-foreground/70">
                Complete job tailoring for one (1) target role, including:
              </p>

              <ul className="mt-4 flex-1 space-y-3 text-left text-sm">
                {CREDIT_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start text-foreground/70">
                    <Check className="mt-0.5 mr-2 size-4 shrink-0 text-success" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-4 text-sm text-foreground/55">
                Remediate Knowledge Gaps in Your Portfolio*
              </p>

              <Button
                onClick={handleSelectSingle}
                disabled={flow === 'processing'}
                variant="outline"
                size="lg"
                className="mt-6 w-full"
              >
                {flow === 'processing' ? 'Loading…' : 'Select Single Job'}
              </Button>
            </div>

            {/* Monthly Subscription */}
            <div className="relative flex h-full flex-col rounded-2xl border border-primary/40 bg-card/80 p-6 shadow-lg shadow-primary/10 sm:p-7">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="rounded-full border border-primary/40 bg-popover px-4 py-1 text-xs font-semibold text-primary">
                  Best Value
                </span>
              </div>

              <h3 className="text-lg font-semibold">Career Catalyst Professional</h3>
              <div className="mt-3 flex items-baseline gap-2.5">
                <span className="text-4xl font-bold tracking-tight">$14.99</span>
                <span className="text-sm text-foreground/55">per month</span>
              </div>
              <p className="mt-4 text-sm text-foreground/70">8 job tailorings per month:</p>

              <ul className="mt-4 space-y-3 text-left text-sm">
                <li className="flex items-start text-foreground/70">
                  <Check className="mt-0.5 mr-2 size-4 shrink-0 text-success" />
                  <span>Everything in Job Tailoring Credit</span>
                </li>
              </ul>

              <p className="mt-4 text-sm font-medium text-primary">PLUS subscriber-only perk:</p>
              <ul className="mt-2 flex-1 space-y-3 text-left text-sm">
                <li className="flex items-start text-foreground/70">
                  <Star className="mt-0.5 mr-2 size-4 shrink-0 text-primary" />
                  <span>Resume regeneration for ANY job</span>
                </li>
              </ul>

              <Button
                onClick={handleSelectSubscription}
                disabled={flow === 'processing'}
                size="lg"
                className={cn(
                  BRAND_GRADIENT,
                  'mt-6 w-full text-white shadow-lg shadow-primary/25 hover:shadow-primary/40'
                )}
              >
                {flow === 'processing' ? 'Loading…' : 'Subscribe Monthly'}
              </Button>
            </div>
          </div>

          {/* Coupon Code Input */}
          <div className="border-t border-border pt-4">
            <div className="mx-auto max-w-md">
              <label
                htmlFor="purchase-coupon-code"
                className="mb-2 block text-center text-sm font-medium text-muted-foreground"
              >
                Have a coupon or promotion code?
              </label>
              <Input
                id="purchase-coupon-code"
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="h-10 text-center"
                disabled={flow === 'processing'}
              />
              {couponCode && (
                <p className="mt-2 text-center text-xs text-foreground/55">
                  Code will be validated when you select a plan
                </p>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="border-t border-border pt-4 text-center text-xs text-foreground/55">
            *Up to 25 messages sent between all AI agents for each job tailoring credit purchased
          </div>
        </div>
      )}

      {/* Show payment form */}
      {(flow === 'payment-single' || flow === 'payment-subscription') && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setFlow('select')}
            className="inline-flex items-center gap-1 rounded-lg text-sm font-medium text-primary outline-hidden hover:underline focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <ArrowLeft className="size-4" />
            Back to plans
          </button>

          {/* Show coupon applied message */}
          {couponApplied && (
            <div className="rounded-xl border border-success/25 bg-success/10 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-success">
                  <Check className="mr-2 size-5" />
                  <span className="font-medium">{couponApplied.name || 'Coupon applied!'}</span>
                </div>
                <span className="font-semibold text-success">{couponApplied.discount}</span>
              </div>
              {originalAmount && originalAmount !== displayAmount && (
                <div className="mt-2 text-sm text-muted-foreground">
                  Original price:{' '}
                  <span className="line-through">${(originalAmount / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {clientSecret && (
            <PaymentForm
              clientSecret={clientSecret}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              amount={displayAmount}
              description={paymentDescription}
            />
          )}
        </div>
      )}

      {/* Show success message */}
      {flow === 'success' && (
        <div className="py-8 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-success/15">
            <Check className="size-8 text-success" />
          </div>
          <h3 className="mb-2 text-2xl font-bold tracking-tight text-foreground">
            Payment Successful!
          </h3>
          <p className="text-muted-foreground">Redirecting you to upload your job posting...</p>
        </div>
      )}
    </BaseModal>
  );
};
